import { EventContextProvider, useAnalytics } from "@codat/analytics";
import {
  CodatLogoBlueSquare,
  IndicatorLabel,
  MegaphoneIcon,
  Button as OrchardButton,
  Skeleton,
} from "@codat/orchard-ui";
import { UserState } from "api/public";
import classNames from "classnames";
import { usePageWidthViewContext } from "contexts/PageWidthViewContext";
import useLocalStorage from "hooks/useLocalStorage";
import { useParseWhatsNewFeed } from "hooks/useParseWhatsNewFeed";
import { useFeatureStatus } from "portal-ui-remote/FeatureStatusContext";
import React, { useEffect, useState } from "react";

import AvatarDropdown from "components/AvatarDropdown";
import CommandSearch from "components/CommandSearch";
import ContactSales from "components/ContactSales";
import ExpandCollapseButton from "components/ExpandCollapseButton";
import Link from "components/Link";
import Modal from "components/Modal";
import NavDropdown from "components/NavDropdown";
import { NavDropdownItem } from "components/NavDropdown/NavDropdown";
import SupportDropdown from "components/SupportDropdown";
import UserClientsDropdown from "components/UserClientsDropdown";

import UpgradeButton from "../UpgradeButton";
import styles from "./NavHeader.module.scss";

interface NavHeaderProps {
  userState: UserState | undefined;
  userFullName?: string;
}

const NavHeader: React.FC<NavHeaderProps> = ({ userState }) => {
  const [showContactSalesModal, setShowContactSalesModal] = useState(false);
  const [whatsNewItems, setWhatsNewItems] = useState<NavDropdownItem[]>([]);
  const [whatsNewCount, setWhatsNewCount] = useState<number>(0);
  const { featureStatus } = useFeatureStatus();
  const { sendEvent } = useAnalytics();

  const {
    data: feedItems,
    loading: isFeedLoading,
    error: isFeedError,
  } = useParseWhatsNewFeed();

  const [lastReadForWhatsNew, setLastReadForWhatsNew] = useLocalStorage<Date>(
    "lastReadForWhatsNew",
    new Date(0)
  );

  const { fullWidthView, setFullWidthView } = usePageWidthViewContext();

  useEffect(() => {
    if (feedItems) {
      const items = feedItems.map((item) => {
        const { link, ...feed } = item;
        return {
          buttonText: "",
          path: undefined,
          permission: true,
          href: link,
          feed,
        };
      });
      setWhatsNewItems(items);
    }
  }, [feedItems, sendEvent]);

  useEffect(() => {
    if (feedItems) {
      const newItems = feedItems.filter((item) => {
        if (item.date) {
          const newsDate = new Date(item.date);
          if (newsDate > new Date(lastReadForWhatsNew)) {
            return item;
          }
        }
      });
      setWhatsNewCount(newItems.length);
    }
  }, [feedItems, lastReadForWhatsNew]);

  const closeContactSalesModal = () => setShowContactSalesModal(false);

  const isLoading = !userState || !featureStatus;

  if (isLoading) {
    return (
      <header data-testid="nav-header" className={styles.header}>
        <div className={styles.logo}>
          <Link linkName="Logo" title="Home" aria-label="Home" to="/">
            <CodatLogoBlueSquare />
          </Link>
        </div>
        <div className={styles.verticalDivider} />
        <div className={styles.client}>
          <Skeleton height="2.5rem" width="12rem" />
        </div>
        <div className={styles.search}>
          <Skeleton height="2.5rem" width="25rem" />
        </div>
        <ul className={styles.list}>
          <span
            className={classNames(styles.listItem, styles.divider)}
            aria-hidden="true"
          />
          {Array.from({ length: 3 }, (_, i) => i).map((x) => (
            <li key={x} className={styles.listItem}>
              <Skeleton
                className={classNames(styles.skeleton, styles.round)}
                height="1rem"
                width="1rem"
              />
            </li>
          ))}
          <li className={styles.listItem}>
            <Skeleton
              className={classNames(styles.skeleton, styles.round)}
              height="2rem"
              width="2rem"
            />
          </li>
        </ul>
      </header>
    );
  }

  const isFreeAccount = userState?.clientType === "ClientFreeTrial";
  const isStartUpAccount = userState?.clientType === "ClientStartUp";
  const hideStartUpPlan = featureStatus?.["PortalUI.HideStartUpPlan"] ?? false;

  const handleWhatsNewWithTracking = () => {
    sendEvent("Whats New Dropdown - Click");
    setLastReadForWhatsNew(new Date());
  };

  return (
    <header data-testid="nav-header" className={styles.header}>
      <div className={styles.logo}>
        <Link linkName="Logo" title="Home" aria-label="Home" to="/">
          <CodatLogoBlueSquare />
        </Link>
      </div>
      <EventContextProvider name="Clients Dropdown">
        <div className={styles.verticalDivider} />
        <UserClientsDropdown userState={userState} />
      </EventContextProvider>

      <div className={styles.search}>
        <CommandSearch />
      </div>

      <ul className={styles.list}>
        {(isFreeAccount || isStartUpAccount) && (
          <li className={styles.listItem}>
            <UpgradeButton
              userState={userState}
              hideStartUpPlan={hideStartUpPlan}
            />
          </li>
        )}

        <span
          className={classNames(styles.listItem, styles.divider)}
          aria-hidden="true"
        />

        <li
          data-testid="full-width-view-setting"
          className={classNames(
            styles.listItem,
            styles.collapseExpandViewToggle
          )}
        >
          <ExpandCollapseButton
            buttonName="Toggle View"
            data-testid="toggle-view-button"
            variant="tertiary"
            color="interface"
            isExpanded={fullWidthView}
            onClick={setFullWidthView}
            aria-label={`${
              fullWidthView ? "Disable" : "Enable"
            } full screen width view`}
            size="small"
            iconClassName={styles.triggerIcon}
          />
        </li>

        <li className={classNames(styles.listItem, styles.whatsNewDropdown)}>
          <NavDropdown
            data-testid="whats-new-dropdown"
            className={styles.popoverSpacer}
            trigger={
              <OrchardButton
                data-testid="whats-new-button"
                variant="tertiary"
                color="interface"
                onClick={handleWhatsNewWithTracking}
                aria-label="Whats new"
                size="small"
                isSkeleton={isFeedLoading}
              >
                <MegaphoneIcon className={styles.triggerIcon} />
                {whatsNewCount > 0 && (
                  <IndicatorLabel
                    color="negative-dark"
                    className={styles.whatsNewCount}
                    label={whatsNewCount.toString()}
                    data-testid="whats-new-count"
                  />
                )}
              </OrchardButton>
            }
            label="Whats new"
            pauseFocusTrap={isFeedError}
            eventProperties={{}}
            isLoading={isFeedLoading}
            header={{
              title: "What's new",
              icon: <MegaphoneIcon className={styles.megaphoneIcon} />,
            }}
            menuItems={whatsNewItems}
            errorMessage={isFeedError ? "No updates found" : ""}
          />
        </li>
        <li className={classNames(styles.listItem, styles.supportDropdown)}>
          <SupportDropdown
            pauseFocusTrap={showContactSalesModal}
            onContactSalesClick={() => setShowContactSalesModal(true)}
            className={styles.popoverSpacer}
          />
        </li>
        <li className={styles.listItem}>
          <AvatarDropdown userState={userState} />
        </li>
      </ul>
      <EventContextProvider name="Contact Sales Modal">
        <Modal onClose={closeContactSalesModal} size="small">
          {showContactSalesModal && (
            <ContactSales onClose={closeContactSalesModal} />
          )}
        </Modal>
      </EventContextProvider>
    </header>
  );
};

export default NavHeader;
